/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/
/* eslint-disable no-unused-expressions */

import React, { useState, useEffect, useRef, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { GlobalLoaderContext, GlobalAuthContext } from "store/context";
import { FieldArray, Form, Formik } from "formik";
import {
  FieldGrid,
  ColIcon,
  TableHeader,
  TableCollum,
  Content,
  InlineLabel,
  MobileLabel,
  RowFilterOrder,
  TableHead,
  TableRow,
  TableBody,
  Table,
  RowFilterIcon,
  FilterSearchContainer,
  RowFilter,
  FilterColumn,
  CheckBoxColumn,
  ButtonContent,
  FieldText,
  FilterContainer,
  Line,
  ButtonContainer,
  FakeButton,
  LoadContainer
} from "./styles";

import { FitinsurAPI, MessageCenter } from "utils";
import Layout from "components/Layout";
import Loader from "components/Loader";
import { Col, Row } from "components/Grid";
import {
  GridColumnTitle,
  InputField,
  Label,
  SelectField,
} from "components/Form";
import Checkbox from "components/Form/CheckBox";
import Icon from "components/Icon";
import { Button } from "components/index";

const ProductPlanPerson = ({ match }) => {
  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);
  const history = useHistory();
  const formik = useRef();
  const location = useLocation();
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();

  const [firstName, setFirstName] = useState(null);
  const [fullName, setFullName] = useState(null);
  const [hierarchy, setHierarchy] = useState(null);
  const [showFilter, setShowFilter] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [products, setProducts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState({
    key: null,
    direction: null,
  });

  const initialValues = {
    filter: { product: "", productPlan: "", productPlanType: "" },
  };

  const columns = [
    {
      title: "PRODUCT",
      value: "product",
      minWidth: 125,
    },
    {
      title: "PRODUCT PLAN",
      value: "productPlan",
    },
    {
      title: "PRODUCT PLAN TYPE",
      value: "productPlanType",
    },
    {
      title: "QUOTATION EXPIRE PERIOD",
      value: "quotationExpirePeriod",
    },
    {
      title: "Action",
    },
  ];

  const getPerson = async () => {
    try {
      const idPerson = match.params.idPerson;

      if (idPerson) { 
        loading.start();
        fitinsur.setAuthToken(authContext.token);
        const response = await fitinsur
          .requestGetPersonsInfo(idPerson)
          .then((res) => res.json());
        if (response.ret_code !== 200) mc.error(response.ret_msg_details);
        loading.stop();
  
        let { firstName, lastName, hierarchy } = response.ret_value;
  
        setFirstName(firstName);
        setFullName(lastName);
        setHierarchy(hierarchy);
      }
    } catch (error) {
      console.log("Error fetch data: ", error);
      loading.stop();
      mc.error("An error happened, please try again.");
    }
  };

  function resetFilters() {
    formik.current.resetForm(initialValues);
    setCurrentPage(1);
    setSortBy({ key: null, direction: null });
    handleFilter(initialValues.filter);
  }

  function handleSortList(key) {
    let direction = "asc";
    if (sortBy.key === key && sortBy.direction === "asc") direction = "desc";
    const sort = { key: key, direction: direction };

    setSortBy(sort);
    setCurrentPage(1);
    getProducts(1, sort, `${location.search}`);
  }

  const jsonToQueryString = (json) => {
    const keys = Object.keys(json).filter((key) => json[key]);
    return keys
      .map((key, index) => {
        const sufix = index + 1 === keys.length ? "" : "&";
        return (
          encodeURIComponent(key) + "=" + encodeURIComponent(json[key]) + sufix
        );
      })
      .join("");
  };

  const handleFilter = (filters) => {
    loading.start();
    const formikFilters = formik.current.values.filter;
    const { ...rest } = formikFilters;

    const filterObject = { ...rest, ...filters };
    const filterQueryString = `?${jsonToQueryString(filterObject)}`;

    setCurrentPage(1);
    getProducts(1, {}, filterQueryString);
    history.push(`${filterQueryString}`);
    loading.stop();
  };

  const handleSetFilterFromParams = () => {
    if (location.search) {
      const query = new URLSearchParams(location.search);

      if (query.get("product")) {
        formik.current?.setFieldValue("filter.product", query.get("product"));
      }

      if (query.get("productPlan")) {
        formik.current?.setFieldValue(
          "filter.productPlan",
          query.get("productPlan")
        );
      }

      if (query.get("productPlanType")) {
        formik.current?.setFieldValue(
          "filter.productPlanType",
          query.get("productPlanType")
        );
      }
    }
  };

  function setNextPage() {
    if (currentPage === totalPages) return;
    setCurrentPage(currentPage + 1);
    getProducts(currentPage + 1, sortBy, `${location.search}`);
  }

  async function getProducts(currentPage = 1, sort = {}, filterQueryString = "?") {
    loading.start();

    try {
      const url = `${filterQueryString || "?"}&sortBy=${JSON.stringify(
        sort
      )}&currentPage=${currentPage}&idPerson=${match.params.idPerson}`;
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestGetProductList(url).then((response) => response.json());
      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details);
        loading.stop();
        return;
      }
      setProducts(response.ret_value);
      setTotalPages(response.pagination_control.totalPages);
      loading.stop();
    } catch (err) {
      console.log(err);
      loading.stop();
    }
  }

  useEffect(() => {
    if (location.search) {
      handleSetFilterFromParams();
      getProducts(1, sortBy, `${location.search}`);
    } else {
      getProducts();
    }
  }, []);

  async function loadContent() {
    await getPerson();
  }

  useEffect(() => {
    loadContent();
  }, [match.params?.idPerson]);


  return (
    <Layout title={fullName || ""}>
      <Row>
        <Col>
          <FieldText size={15}>Short Name</FieldText>
          <FieldText bold>{firstName}</FieldText>
        </Col>
        <Col>
          <FieldText size={15}>Hierarchy</FieldText>
          <FieldText bold>{hierarchy}</FieldText>
        </Col>
      </Row>
      <Row style={{ marginTop: "35px" }}>
        <Col>
          <FieldText bold>PRODUCTS PLANS</FieldText>
          <Line />
          <FakeButton onClick={() => history.push(`/product-plan/edit-product-plan/person/${match.params.idPerson}`)}>
            <Icon name="add" className="icon-add" />
            <FieldText>New Register</FieldText>
          </FakeButton>
        </Col>
      </Row>
      <Content>
        <Formik
          initialValues={initialValues}
          innerRef={formik}
          onSubmit={(values) => handleFilter(values)}
        >
          {(formikProps) => (
            <Form style={{ minHeight: showFilter ? "250px" : "" }}>
              <RowFilterIcon>
                <FilterContainer
                  className={showFilter ? "filter-open" : "filter-close"}
                >
                  <Row>
                    <Col
                      style={{ display: "flex", justifyContent: "flex-end" }}
                    >
                      <Icon
                        name="close"
                        className="icon-close"
                        onClick={() => setShowFilter(false)}
                      />
                    </Col>
                  </Row>

                  <RowFilterOrder>
                    <MobileLabel>ORDER</MobileLabel>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        padding: "10px",
                      }}
                    >
                      {showFilter &&
                        columns.map((column, index) => (
                          <Row key={index}>
                            <GridColumnTitle
                             onClick={() => column.value && handleSortList(column.value)}
                              style={{
                                cursor: column.value
                                  ? "pointer"
                                  : "not-allowed",
                              }}
                              className="mb-2 mr-2"
                            >
                              {column.title}
                            </GridColumnTitle>
                          </Row>
                        ))}
                    </div>
                  </RowFilterOrder>
                  <hr className="d-md-none mb-3" />
                  <FilterSearchContainer>
                    <Row>
                      <MobileLabel>FILTER</MobileLabel>
                    </Row>
                    <RowFilter>
                      <CheckBoxColumn>
                        <Label>
                          <Checkbox
                           checked={formikProps.values.filter.product.length}
                           onChange={() => formik.current?.setFieldValue("filter.product", "")}
                          />
                        </Label>
                      </CheckBoxColumn>
                      <FilterColumn>
                        <FieldGrid
                          name="filter.product"
                          placeholder="Product"
                          component={InputField}
                          validation={false}
                        />
                      </FilterColumn>
                    </RowFilter>
                    <RowFilter>
                      <CheckBoxColumn>
                        <Label>
                          <Checkbox
                            checked={formikProps.values.filter.productPlan.length}
                            onChange={() => formik.current?.setFieldValue("filter.productPlan", "")}
                          />
                        </Label>
                      </CheckBoxColumn>
                      <FilterColumn>
                        <FieldGrid
                          name="filter.productPlan"
                          placeholder="Product Plan"
                          component={InputField}
                          validation={false}
                        />
                      </FilterColumn>
                    </RowFilter>

                    <RowFilter>
                      <CheckBoxColumn>
                        <Label>
                          <Checkbox
                           checked={formikProps.values.filter.productPlanType}
                           onChange={() => formik.current?.setFieldValue("filter.productPlanType")}
                          />
                        </Label>
                      </CheckBoxColumn>
                      <FilterColumn>
                      <FieldGrid
                          name="filter.productPlanType"
                          placeholder="Product Plan Type"
                          component={SelectField}
                          validation={false}
                          options={[
                            { label: "Calculation", value: "calculation" },
                            { label: "Closed Plan", value: "closedPlan" },
                          ]}
                          onChange={(productPlanType) => handleFilter({ productPlanType })}
                        />
                      </FilterColumn>
                    </RowFilter>
                  </FilterSearchContainer>

                  <Row align="center" className="apply-button-container">
                    <Col>
                      <Button
                        primary
                        type="button"
                        id="btnFilter"
                        style={{ textAlign: "center" }}
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setShowFilter(false);
                          handleFilter();
                        }}
                      >
                        Apply
                      </Button>
                    </Col>
                  </Row>
                </FilterContainer>

                <ColIcon>
                  <Icon
                    name="filter"
                    onClick={(e) => setShowFilter(!showFilter)}
                  />
                </ColIcon>
                <ColIcon>
                  <Icon name="filterCan" onClick={() => resetFilters()}/>
                </ColIcon>
              </RowFilterIcon>

              <div style={{ marginLeft: 15, marginRight: 15 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      {columns.map((column, index) => (
                        <TableHeader
                          key={index}
                          minWidth={column.minWidth}
                          maxWidth={column.maxWidth}
                        >
                          <GridColumnTitle
                            onClick={() =>
                              column.value && handleSortList(column.value)
                            }
                            style={{
                              cursor: column.value ? "pointer" : "not-allowed",
                            }}
                          >
                            {column.title}
                          </GridColumnTitle>
                        </TableHeader>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <FieldArray
                      name="productplan"
                      render={() =>
                        products && products.map((product, index) => (
                          <TableRow key={index}>
                            <TableCollum>
                              <InlineLabel>PRODUCT</InlineLabel>
                              <Label fontSize={12}>{product.product || ""}</Label>
                            </TableCollum>

                            <TableCollum>
                              <InlineLabel>PRODUCT PLAN</InlineLabel>
                              <Label wordWrap fontSize={12}>
                                {product.productPlan || ""}
                              </Label>
                            </TableCollum>

                            <TableCollum>
                              <InlineLabel>PRODUCT PLAN TYPE</InlineLabel>
                              <Label wordWrap fontSize={12}>
                                {product.productPlanType || ""}
                              </Label>
                            </TableCollum>

                            <TableCollum>
                              <InlineLabel>QUOTATION EXPIRE PERIOD</InlineLabel>
                              <Label wordWrap fontSize={12}>
                                {product.quotationExpirePeriod || ""}
                              </Label>
                            </TableCollum>

                            <TableCollum
                              style={{ textAlign: "right", paddingRight: 10 }}
                            >
                              <InlineLabel>Action</InlineLabel>
                              <Label>
                                <Icon
                                  onClick={() => history.push(`/product-plan/edit-product-plan/person/${match.params.idPerson}/${product.idProduct}/${product.idProductPlan}`)}
                                  name="edit"
                                  style={{
                                    alignSelf: "left",
                                    cursor: "pointer",
                                  }}
                                />
                              </Label>
                            </TableCollum>
                          </TableRow>
                        ))
                      }
                    />
                  </TableBody>
                </Table>
              </div>
              <ButtonContainer>
                <ButtonContainer
                  style={{
                    justifyContent: "flex-start",
                    alignItems: "center",
                    paddingBottom: "10px",
                  }}
                >
                  <ButtonContent>
                    <Button
                      forth
                      onClick={() => history.goBack()}
                      style={{ alignSelf: "flex-start" }}
                    >
                      {" "}
                      &lt; Back
                    </Button>
                  </ButtonContent>
                </ButtonContainer>
              </ButtonContainer>
            </Form>
          )}
        </Formik>
        <LoadContainer>
          {!products || products.length === 0 || currentPage === totalPages ? (
            <Button forth onClick={(e) => e.preventDefault()}>
              NO MORE ITEMS
            </Button>
          ) : (
            <Button forth onClick={() => setNextPage()}>
              +LOAD MORE ACCOUNTS
            </Button>
          )}
        </LoadContainer>
      </Content>
    </Layout>
  );
};

export default ProductPlanPerson;
